import { formatScore } from "./format"
import type { Corte, Faixa, Oferta, ProvaSocial, SubjectResult } from "../types"

/**
 * A narrativa do produto, espelho de app/services/narrativa.rb: o simulado é o
 * mecanismo, a transformação é chegar à prova sabendo que classifica, e o que
 * se vende é um método com nome — o Método Linha de Corte (07/10/2026). Uma
 * frase só para cada coisa, usada em toda tela — nunca reescrita no lugar.
 *
 * Regra de nomes: UM nome próprio novo em tela (Linha de Corte) mais os nomes
 * que o app já usa (Nivelamento, Diagnóstico, Fortalecimento, Oficial). Tudo o
 * mais em verbo.
 *
 * Vocabulário proibido na manchete: "acesso", "ilimitado", "dias", "todos os
 * simulados", "pacote", "plano". Podem existir como detalhe, ao lado do preço.
 */
export const PROMESSA = "Chegue à prova sabendo que classifica."
export const METODO = "Método Linha de Corte"
export const METODO_CURTO = "Linha de Corte"
export const MECANISMO = "Saiba a quantos pontos está do corte, treine onde perde ponto e descubra quando está pronto."
export const CRITERIO_DE_PRONTO = "Pronto é passar do corte do último concurso em dois simulados oficiais seguidos."

/** O método: cinco etapas, duas grátis. Mesma lista de Narrativa::ETAPAS. */
export const ETAPAS_GRATIS = "Etapas 1 e 2 grátis."
export interface Etapa {
  n: number
  nome: string
  gratis: boolean
  frase: string
}
export const ETAPAS: Etapa[] = [
  { n: 1, nome: "Nivelamento", gratis: true, frase: "10 questões, todas as matérias, sem estudar antes: veja a quantos pontos está do corte" },
  { n: 2, nome: "Diagnóstico", gratis: true, frase: "10 questões só da matéria em que você mais perde ponto" },
  { n: 3, nome: "Fortalecimento", gratis: false, frase: "treinos que puxam mais questões de onde você perde ponto" },
  { n: 4, nome: "Oficial", gratis: false, frase: "a prova inteira, no formato e no tempo da banca, quantas vezes precisar" },
  { n: 5, nome: "Linha de Corte", gratis: false, frase: "pronto é passar do corte da sua lista em dois oficiais seguidos" },
]

/** Oferta: um plano, pago uma vez. Preço SEMPRE vem de me.oferta (banco), nunca daqui. */
export const PARCELAS = 3
export const VALIDADE_LINHA = "Vale 365 dias ou até 30 dias depois da sua prova, o que for mais longe. Nada renova sozinho."
/** A única garantia: a do CDC (decisão do dono, 06/10/2026). */
export const GARANTIA = "7 dias de arrependimento, reembolso integral."

/**
 * Diagnóstico → tratamento (decisão de 08/09/2026). "Nivelamento" segue sendo
 * o nome do quiz; "Diagnóstico gratuito" é o nome do pacote grátis (etapas 1 e
 * 2). "Treino" nomeia só o que o tratamento destrava.
 */
export const DIAGNOSTICO = "Diagnóstico gratuito"
export const TRATAMENTO_LEAD = "Diagnóstico mostra onde. Tratamento leva até lá."
export const DIAGNOSTICO_SUBTITULO = "Descubra suas matérias de partida em 10 questões."
export const DIAGNOSTICO_ESCOPO = "o nivelamento e um diagnóstico por matéria"
export const TRATAMENTO_CONTRASTE = "O diagnóstico mostrou o problema. O tratamento fecha a distância."
export const TRATAMENTO_CONTRASTE_ACIMA = "O diagnóstico mostrou que dá. O tratamento transforma isso em consistência."
/** Uma palavra por ação nos CTAs de funil: grátis = diagnóstico, pago = tratamento. */
export const CTA_DIAGNOSTICO = "Fazer meu diagnóstico grátis"
export const CTA_TRATAMENTO = "Começar meu tratamento"
export const CTA_TRATAMENTO_CONTINUAR = "Continuar meu tratamento"
/** O toast do 402 e o cartão bloqueado — espelho de Narrativa::TRATAMENTO_REQUERIDO. */
export const TRATAMENTO_REQUERIDO = "Esse formato faz parte do tratamento"

/** Procedência: onde a IA entra (no acervo, nunca ao vivo). */
export const PROCEDENCIA =
  "As questões que entram pelo nosso motor são resolvidas às cegas por IA e comparadas com o gabarito da banca antes de entrar; o que diverge vai para revisão humana."
export const PROCEDENCIA_LIMITE =
  "A IA trabalha antes, no acervo. Na hora do seu simulado quem decide é a conta do edital e o seu histórico — nada é gerado ao vivo."
/** Honestidade estatística: o motor só usa o acerto de uma matéria depois disto. */
export const MAPEADA_MIN = 20
export const RITMO_NOTA = "tempo até a última marcação de cada questão"

/**
 * Desarma a vergonha de errar no nivelamento com número nosso. REMEDIDO em
 * 07/10/2026 sobre o primeiro simulado de 127 usuários reais na ESA: erro
 * médio 78,1%, mediana 2,0, 69% erraram 70% ou mais. Espelho de
 * Narrativa::ERRO_NO_PRIMEIRO — remedir antes de mexer no número.
 */
export const ERRO_NO_PRIMEIRO = "a maioria erra 70% no primeiro"
export const OBJECAO_NUMERO = "quem faz o teste antes de estudar acerta mais depois: 75% contra 56% de quem só estudou o texto"
export const OBJECAO_FONTE = "https://doi.org/10.1037/a0016496"
export const OBJECAO_FONTE_LABEL = "Richland, Kornell e Kao, 2009"

/** As listas da ESA — rótulos iguais aos de Narrativa::LISTAS. */
export const LISTAS: Record<string, string> = {
  masc_ampla: "masculina, ampla concorrência",
  masc_cotas: "masculina, cotas",
  fem_ampla: "feminina, ampla concorrência",
  fem_cotas: "feminina, cotas",
}

export function promessaPara(sigla: string): string {
  return `Chegue à ${sigla} sabendo que classifica.`
}

/** Os quatro pilares da preparação, na ordem do ciclo. O quarto é o critério. */
export function pilares(criterio: string = CRITERIO_DE_PRONTO): [string, string][] {
  return [
    // NÃO chamar de "nivelamento": esse é o nome do quiz grátis, e vender o
    // grátis como feature do plano desmonta a metáfora.
    ["Diagnóstico a cada simulado", "matéria por matéria, com a nota pela fórmula do edital: descubra exatamente o que está derrubando sua nota."],
    ["Estudo pelos erros", "cada questão com a resposta certa e o comentário — a aula está no gabarito."],
    ["Treino no formato da banca", "o mesmo número de questões, o mesmo tempo e a mesma distribuição da prova, quantas vezes precisar."],
    ["Saber quando está pronto", `${criterio} Aí ${PROMESSA.charAt(0).toLowerCase()}${PROMESSA.slice(1)}`],
  ]
}

/** Corte e nota de banca: duas casas ("4,98"), porque o corte tem milésimos. */
export function formatCorte(nota: number): string {
  return nota.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/**
 * Passaria do corte? SÓ com a nota pela fórmula da banca e SÓ com o corte da
 * lista do candidato. Sem um dos dois, null — e null nunca vira "não passou".
 */
export function passariaDoCorte(notaBanca: number | null | undefined, corte: Corte | null): boolean | null {
  if (notaBanca == null || !corte) return null
  return notaBanca >= corte.nota
}

/**
 * Quanto falta pro corte, só quando a pessoa ficou abaixo dele. No nivelamento
 * (10 questões, ~2 por parte) a decimal seria precisão falsa: arredonda a meio
 * ponto e diz "cerca de". Nos demais, uma casa.
 */
export function distanciaDoCorte(
  notaBanca: number | null | undefined,
  corte: Corte | null,
  opts: { aproximada?: boolean } = {},
): string | null {
  if (notaBanca == null || !corte || notaBanca >= corte.nota) return null
  const bruta = corte.nota - notaBanca
  if (opts.aproximada) {
    const d = Math.max(0.5, Math.round(bruta * 2) / 2)
    const n = d === Math.floor(d) ? String(d) : formatScore(d)
    return `cerca de ${n} ${d === 1 ? "ponto" : "pontos"}`
  }
  const d = Math.round(bruta * 10) / 10
  return `${formatScore(d)} ${d === 1 ? "ponto" : "pontos"}`
}

/**
 * Matérias em que a pessoa perdeu ponto, da mais fraca para a menos fraca —
 * a lista INTEIRA. Citar só duas enquanto a tabela mostra cinco faz parecer
 * que o sistema não viu as outras.
 */
export function materiasDePartida(results: SubjectResult[]): SubjectResult[] {
  return [...results]
    .filter((r) => r.total > 0 && r.correct < r.total)
    .sort((a, b) => a.correct / a.total - b.correct / b.total)
}

export function materiasFechadas(results: SubjectResult[]): SubjectResult[] {
  return results.filter((r) => r.total > 0 && r.correct === r.total)
}

/** Todas as matérias, nome por nome. */
export function listarMaterias(fracas: SubjectResult[]): string {
  const nomes = fracas.map((r) => r.subject)
  if (nomes.length <= 1) return nomes.join("")
  return `${nomes.slice(0, -1).join(", ")} e ${nomes[nomes.length - 1]}`
}

/** Até três, nome por nome; acima disso o número diz mais. */
export function nomearLacunas(fracas: SubjectResult[]): string {
  const nomes = fracas.map((r) => r.subject)
  if (nomes.length > 3) return `as ${nomes.length} matérias em que você perdeu ponto`
  if (nomes.length <= 1) return nomes.join("")
  return `${nomes.slice(0, -1).join(", ")} e ${nomes[nomes.length - 1]}`
}

/**
 * "2.157 questões comentadas: 1.963 de provas oficiais, de 2013 a 2026, e 194
 * autorais" — o que o banco sustenta, separando as duas origens (há questões
 * autorais no sorteio por decisão do dono).
 */
export function provaSocialLabel(ps: ProvaSocial | undefined): string | null {
  if (!ps || ps.questoes <= 0) return null
  const n = (x: number) => x.toLocaleString("pt-BR")
  const anos = ps.ano_inicial && ps.ano_final && ps.ano_inicial !== ps.ano_final ? `, de ${ps.ano_inicial} a ${ps.ano_final}` : ""
  if (!ps.autorais) return `${n(ps.questoes)} questões de provas oficiais comentadas${anos}`
  if (!ps.oficiais) return `${n(ps.questoes)} questões comentadas`
  return `${n(ps.questoes)} questões comentadas: ${n(ps.oficiais)} de provas oficiais${anos} e ${n(ps.autorais)} autorais`
}

/** "entre 7,5 e 8,0" — faixa de referência, nunca apresentada como o corte. */
export function faixaLabel(faixa: Faixa): string {
  return `entre ${formatScore(faixa.de)} e ${formatScore(faixa.ate)}`
}

/** "4,98 em 2025 (masculina, ampla concorrência)". */
export function corteLabel(corte: Corte): string {
  const lista = corte.rotulo ? ` (${corte.rotulo})` : ""
  return `${formatCorte(corte.nota)} em ${corte.ano}${lista}`
}

/** A linha de preço de toda tela fora de /plano — vem pronta da API (me.oferta). */
export function precoLinha(oferta: Oferta | null | undefined): string | null {
  return oferta?.linha ?? null
}
